import React, { useEffect, useState } from 'react';
import { theme } from '../../theme';
import { useResponsive } from '../../hooks/useResponsive';
import { TeamColorPicker } from './TeamColorPicker';
import {
  TEAM_COLORS,
  areTeamColorsEqual,
  DEFAULT_TEAM1_COLOR,
  DEFAULT_TEAM2_COLOR,
} from '../../utils/teamColors';

interface EditTeamNamesModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSave: (data: {
    team1Name: string;
    team2Name: string;
    team1Color: string;
    team2Color: string;
  }) => void;
  currentTeam1Name: string;
  currentTeam2Name: string;
  currentTeam1Color?: string;
  currentTeam2Color?: string;
  loading?: boolean;
}

export const EditTeamNamesModal: React.FC<EditTeamNamesModalProps> = ({
  isOpen,
  onClose,
  onSave,
  currentTeam1Name,
  currentTeam2Name,
  currentTeam1Color,
  currentTeam2Color,
  loading = false,
}) => {
  const { isMobile } = useResponsive();
  const [team1Name, setTeam1Name] = useState(currentTeam1Name);
  const [team2Name, setTeam2Name] = useState(currentTeam2Name);
  const [team1Color, setTeam1Color] = useState(currentTeam1Color || DEFAULT_TEAM1_COLOR);
  const [team2Color, setTeam2Color] = useState(currentTeam2Color || DEFAULT_TEAM2_COLOR);

  useEffect(() => {
    if (isOpen) {
      setTeam1Name(currentTeam1Name);
      setTeam2Name(currentTeam2Name);
      setTeam1Color(currentTeam1Color || DEFAULT_TEAM1_COLOR);
      setTeam2Color(currentTeam2Color || DEFAULT_TEAM2_COLOR);
    }
  }, [isOpen, currentTeam1Name, currentTeam2Name, currentTeam1Color, currentTeam2Color]);

  const pickFallback = (used: string): string => {
    const alt = TEAM_COLORS.find((c) => !areTeamColorsEqual(c.hex, used));
    return alt ? alt.hex : DEFAULT_TEAM2_COLOR;
  };

  const handleTeam1Color = (hex: string) => {
    setTeam1Color(hex);
    if (areTeamColorsEqual(hex, team2Color)) {
      setTeam2Color(pickFallback(hex));
    }
  };

  const handleTeam2Color = (hex: string) => {
    setTeam2Color(hex);
    if (areTeamColorsEqual(hex, team1Color)) {
      setTeam1Color(pickFallback(hex));
    }
  };

  const handleSave = () => {
    if (!team1Name.trim() || !team2Name.trim()) return;
    if (areTeamColorsEqual(team1Color, team2Color)) return;
    onSave({
      team1Name: team1Name.trim(),
      team2Name: team2Name.trim(),
      team1Color,
      team2Color,
    });
  };

  const handleClose = () => {
    setTeam1Name(currentTeam1Name);
    setTeam2Name(currentTeam2Name);
    setTeam1Color(currentTeam1Color || DEFAULT_TEAM1_COLOR);
    setTeam2Color(currentTeam2Color || DEFAULT_TEAM2_COLOR);
    onClose();
  };

  if (!isOpen) return null;

  const sameColor = areTeamColorsEqual(team1Color, team2Color);
  const disableSave =
    loading || !team1Name.trim() || !team2Name.trim() || sameColor;

  const inputStyle = {
    padding: isMobile ? '12px' : `${theme.spacing.sm} ${theme.spacing.md}`,
    border: `2px solid ${theme.colors.border}`,
    borderRadius: theme.borderRadius.input,
    fontSize: isMobile ? '15px' : theme.fonts.sizeBody,
    fontFamily: theme.fonts.body,
    backgroundColor: theme.colors.background,
    color: theme.colors.text,
    transition: 'all 0.2s ease',
    width: '100%',
  };

  const teamRow = (
    name: string,
    setName: (v: string) => void,
    color: string,
    onColor: (hex: string) => void,
    other: string,
    label: string,
    inputId: string,
    placeholder: string
  ) => (
    <div style={{ display: 'flex', flexDirection: 'column', gap: theme.spacing.sm }}>
      <label
        htmlFor={inputId}
        style={{
          color: theme.colors.text,
          fontFamily: theme.fonts.body,
          fontWeight: 600,
          fontSize: theme.fonts.sizeBody,
        }}
      >
        {label}
      </label>
      <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
        <input
          id={inputId}
          type="text"
          value={name}
          onChange={(e) => setName(e.target.value)}
          placeholder={placeholder}
          required
          style={{ ...inputStyle, flex: 1 }}
        />
        <TeamColorPicker
          variant="inline"
          label={label}
          selectedColor={color}
          onColorSelect={onColor}
          disabledColor={other}
        />
      </div>
    </div>
  );

  return (
    <div
      style={{
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        backgroundColor: 'rgba(0, 0, 0, 0.7)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        zIndex: 1000,
        padding: isMobile ? '16px' : '24px',
      }}
    >
      <div
        style={{
          backgroundColor: theme.colors.surface,
          borderRadius: theme.borderRadius.card,
          padding: isMobile ? '20px' : theme.spacing.lg,
          boxShadow: theme.colors.shadow,
          border: `2px solid ${theme.colors.primary}`,
          maxWidth: isMobile ? '100%' : '520px',
          width: '100%',
          maxHeight: '90vh',
          overflowY: 'auto',
        }}
      >
        <h2
          style={{
            margin: '0 0 24px 0',
            color: theme.colors.primary,
            fontFamily: theme.fonts.heading,
            fontSize: isMobile ? '20px' : theme.fonts.sizeTitle,
            letterSpacing: theme.fonts.letterSpacingWide,
            textTransform: 'uppercase',
            textAlign: 'center',
          }}
        >
          ✏️ Editar Equipos
        </h2>

        <div style={{ display: 'flex', flexDirection: 'column', gap: theme.spacing.lg }}>
          {teamRow(
            team1Name,
            setTeam1Name,
            team1Color,
            handleTeam1Color,
            team2Color,
            'Equipo 1',
            'edit-team1-name',
            'Ej: Los Tigres'
          )}
          {teamRow(
            team2Name,
            setTeam2Name,
            team2Color,
            handleTeam2Color,
            team1Color,
            'Equipo 2',
            'edit-team2-name',
            'Ej: Los Leones'
          )}
        </div>

        {sameColor && (
          <p
            style={{
              margin: `${theme.spacing.md} 0 0`,
              fontSize: 13,
              color: theme.colors.danger,
              textAlign: 'center',
              fontWeight: 600,
            }}
          >
            ⚠️ Los equipos no pueden tener el mismo color
          </p>
        )}

        <div
          style={{
            display: 'flex',
            gap: theme.spacing.md,
            justifyContent: 'center',
            marginTop: theme.spacing.lg,
          }}
        >
          <button
            onClick={handleClose}
            disabled={loading}
            style={{
              padding: `${theme.spacing.md} ${theme.spacing.lg}`,
              backgroundColor: theme.colors.secondary,
              color: theme.colors.white,
              border: 'none',
              borderRadius: theme.borderRadius.button,
              fontSize: theme.fonts.sizeBody,
              fontFamily: theme.fonts.body,
              fontWeight: 600,
              cursor: loading ? 'not-allowed' : 'pointer',
              opacity: loading ? 0.6 : 1,
              transition: 'all 0.2s ease',
            }}
          >
            ❌ Cancelar
          </button>
          <button
            onClick={handleSave}
            disabled={disableSave}
            style={{
              padding: `${theme.spacing.md} ${theme.spacing.lg}`,
              backgroundColor: disableSave ? theme.colors.secondary : theme.colors.primary,
              color: theme.colors.white,
              border: 'none',
              borderRadius: theme.borderRadius.button,
              fontSize: theme.fonts.sizeBody,
              fontFamily: theme.fonts.body,
              fontWeight: 600,
              cursor: disableSave ? 'not-allowed' : 'pointer',
              opacity: disableSave ? 0.6 : 1,
              transition: 'all 0.2s ease',
            }}
          >
            {loading ? '🔄 Guardando...' : '💾 Guardar'}
          </button>
        </div>
      </div>
    </div>
  );
};
